import {configureStore} from '@reduxjs/toolkit';

import api, {
    CALL_API,
    DOWNLOAD_FILE,
    UPLOAD_FILE,
    downloadFile,
    handleErrors,
    printFile,
    responseToPayload,
    uploadFile,
} from '@pb/middleware-api';

import {errorsHandler} from './errors-handler';
import getRoot from './getRoot';
import {rootReducer} from './root-reducer';

import type {AppState} from './typedef';
import type {ApiAction, ApiDispatch, Dispatch, Types} from '@pb/middleware-api';
import type {Middleware, Store} from '@reduxjs/toolkit';
import type {ApiDispatchForToolkit} from 'src/openapi/admin/types';


const API_ROOT = getRoot();

type ApiActionType = ApiAction<Types, Record<string, never>>;
type ApiMiddlewareAction = Parameters<ReturnType<ReturnType<ReturnType<typeof api>>>>[0];
type ApiErrorDispatch = Parameters<ReturnType<ReturnType<typeof handleErrors>>>[0];
type ApiErrorAction = Parameters<ReturnType<ReturnType<ReturnType<typeof handleErrors>>>>[0];

const isSecureAction = (action: ApiAction<Types, unknown> | unknown): action is ApiActionType => {
    let secure = false;

    if (action && typeof action === 'object') {
        const selectedApi = [
            CALL_API,
            DOWNLOAD_FILE,
            UPLOAD_FILE,
        ].find(possibleApi => possibleApi in action);

        if (selectedApi) {
            const apiSection = (action as Record<typeof selectedApi, {endpoint: string}>)[selectedApi];
            if (!apiSection) {
                return secure;
            }

            const endpoint: string = apiSection.endpoint;
            const OPEN_ENDPOINTS = [
                '/web-api/authentication-config',
                '/web-api/sentry-config',
                '/web-api/statsig-client-config',
            ];
            secure = !OPEN_ENDPOINTS.includes(endpoint);
        }
    }

    return secure;
};

const apiMiddleware: Middleware<ApiDispatchForToolkit, unknown> = middlewareApi => next => async action => {
    const tokenStorageString = localStorage.getItem('okta-token-storage');

    if (tokenStorageString) {
        const tokenStorage = JSON.parse(tokenStorageString);
        const idToken: string | undefined = tokenStorage?.idToken?.idToken;

        if (idToken && isSecureAction(action)) {
            const payload = (action[CALL_API] || action[DOWNLOAD_FILE] || action[UPLOAD_FILE]) as {
                headers?: Record<string, string>,
            };
            if (payload) {
                payload.headers = {...payload.headers, Authorization: `Bearer ${idToken}`};
            }
        }
    }

    return await api({
        apiRoot: API_ROOT,
        downloadFile,
        uploadFile,
        printFile,
    })(middlewareApi as Store)(next as Dispatch<unknown> | ApiDispatch<unknown>)(action as ApiMiddlewareAction);
};

const errorMiddleware: Middleware = middlewareApi => next => async action => (
    await handleErrors(errorsHandler)(middlewareApi as Store)(next as ApiErrorDispatch)(action as ApiErrorAction)
);

const commonMiddleware = [
    apiMiddleware,
    errorMiddleware,
    responseToPayload as Middleware,
];

export const getStore = () => (
    configureStore({
        reducer: rootReducer,
        middleware: getDefaultMiddleware => (
            getDefaultMiddleware({serializableCheck: false}).concat(commonMiddleware)
        ),
    })
);

export const getStoreWithoutDefaults = (preloadedState: Partial<AppState> = {}) => (
    configureStore({
        reducer: rootReducer,
        middleware: getDefaultMiddleware => (
            getDefaultMiddleware({serializableCheck: false}).concat(commonMiddleware)
        ),
        preloadedState,
    })
);

export const store = getStore();
